import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { adminKYCApi } from '@/api/admin/kyc';
import { scrollToTop } from '@/routing';
import { useScrollToTop } from '@/hooks';
import {
  ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN,
  SHOWED_SECTIONS_COUNT,
  STATUS
} from '@/utils/static/kyc';
import { RejectModal } from '@/modules/Questionnaire/modals/RejectModal';
import { kycHelpers } from '@/utils/helpers/kyc';
import { useTranslation } from 'react-i18next';
import { usePopups, useEntityStatus, useLoadingInit, useLoadingState } from '@/hooks';
import { QuestionnaireView } from '@/modules';

export const QuestionnaireAnswers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id, questionnaireId } = useParams();
  const { state } = useLocation();
  const { showErrorToast, showInfoToast } = usePopups();
  const [currentSection, setCurrentSection] = useState(0);
  const [rejectedQuestions, setRejectedQuestions] = useState(null);
  const [isModalActive, setModalActive] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [preFilledQuestions, setPreFilledQuestions] = useState({});
  const [creators, setCreators] = useState({});
  const [defaultValues, setDefaultValues] = useState(null);

  const {
    data: res,
    refetch: refetchQuest,
    isFetching
  } = useQuery({
    queryKey: ['getQuestionnaireAnswers', id, questionnaireId],
    queryFn: () =>
      adminKYCApi.getQuestionnaireAnswers({ investorId: id, questionnaireId: questionnaireId }),
    cacheTime: Infinity,
    onSuccess: async ({ data: questionnaire }) => {
      try {
        if (
          questionnaire?.status === STATUS.QUESTION_UNACCEPTED ||
          questionnaire?.status === STATUS.REJECTED
        ) {
          const res = await adminKYCApi.getRejectedEntities(id);
          const rejectedQuestions = res?.data?.question;

          const { rejectedObj } = kycHelpers.getRejectedObject(questionnaire, rejectedQuestions);

          if (rejectedObj) setRejectedQuestions(rejectedObj);
        }
      } catch (e) {
        showErrorToast(e, true);
      }
    }
  });
  const { mutateAsync: setQuestionnaireStatus, isLoading } = useMutation({
    mutationFn: ({ status, comment }) =>
      adminKYCApi.setQuestionnaireStatus({
        investorId: id,
        status: status,
        questionnaireId: questionnaireId,
        comment: comment
      })
  });

  const questionnaire = useMemo(() => (res ? res.data : null), [res]);

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, submitCount },
    reset
  } = useForm({ defaultValues: { ...defaultValues } });

  useEffect(() => {
    try {
      if (questionnaire) {
        const { values, lastAnsweredSection, preFilledQuestions, creators } =
          kycHelpers.collectQuestionnaireAnswers(questionnaire);

        if (Object.keys(preFilledQuestions).length > 0) {
          setPreFilledQuestions(preFilledQuestions);
        }
        if (Object.keys(creators).length > 0) {
          setCreators(creators);
        }

        setDefaultValues(values);
        reset(values);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  }, [questionnaire]);

  const watchedFields = useWatch({
    control,
    name: questionnaire
      ? questionnaire.questionSections.flatMap(
          (step) => step.visibleConditions?.map((condition) => condition.id) || []
        )
      : []
  });

  useLoadingInit();
  useLoadingState(isFetching);

  useScrollToTop([currentSection]);

  const stepsCount = useMemo(() => {
    if (questionnaire && questionnaire.questionSections && watchedFields) {
      const sections = questionnaire.questionSections.filter((item) =>
        kycHelpers.conditioningSteps(watchedFields, item.visibleConditions, item.visibleOperator)
      );
      return Math.ceil(sections.length / SHOWED_SECTIONS_COUNT);
    }
    return 0;
  }, [questionnaire, watchedFields]);

  const { isRejected, isApproved } = useEntityStatus(res?.data);

  const statusSettings = useMemo(() => {
    if (res && res.data) {
      const status = res?.data?.status;
      return {
        isRejected,
        isApproved,
        allowReject: !isRejected && ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN[status],
        allowApprove: !isApproved && ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN[status]
      };
    }

    return null;
  }, [res, isRejected, isApproved]);

  const rejectQuestionnaire = async () => {
    try {
      if (comment) {
        await setQuestionnaireStatus({ status: STATUS.REJECTED, comment });
        showInfoToast('Rejected');
        setModalActive(false);
        await refetchQuest();
      } else {
        setCommentError(t('Required'));
      }
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    }
  };
  const acceptQuestionnaire = async () => {
    try {
      await setQuestionnaireStatus({ status: STATUS.PASSED });
      showInfoToast('Accepted');
      await refetchQuest();
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    }
  };

  const onNext = () => {
    if (currentSection + 1 < stepsCount) {
      setCurrentSection(currentSection + 1);
      scrollToTop();
    }
  };
  const onBack = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      scrollToTop();
    } else {
      state && state.prevPath ? navigate(state.prevPath) : navigate(-1);
    }
  };

  const addRejectedQuestion = (questionId, comment) => {
    setRejectedQuestions({ ...rejectedQuestions, [questionId]: comment });
  };

  const filteredQuestionnaire = useMemo(() => {
    if (res && res.data && res.data.questionSections)
      return res.data.questionSections
        .filter((item) =>
          kycHelpers.conditioningSteps(watchedFields, item.visibleConditions, item.visibleOperator)
        )
        .sort((a, b) =>
          a.newNumber ? a.newNumber - b.newNumber : a.number ? a.number - b.newNumber : 0
        );
    return [];
  }, [res, currentSection]);

  return (
    <div>
      {!isFetching && res?.data && (
        <QuestionnaireView
          questionnaire={res.data}
          filteredQuestionnaire={filteredQuestionnaire}
          currentSection={currentSection}
          onNext={onNext}
          onBack={onBack}
          adminView
          rejectQuestionnaire={() => setModalActive(true)}
          acceptQuestionnaire={acceptQuestionnaire}
          rejectedQuestions={rejectedQuestions}
          addRejectedQuestion={addRejectedQuestion}
          statusSettings={statusSettings}
          stepsCount={stepsCount}
          setCurrentSection={setCurrentSection}
          disableAccept={isLoading}
          control={control}
          handleSubmit={handleSubmit}
          trigger={trigger}
          watch={watch}
          errors={errors}
          submitCount={submitCount}
          preFilledQuestions={preFilledQuestions}
          creators={creators}
          refetchQuest={refetchQuest}
        />
      )}
      {!isFetching && !res?.data && <div>{t('Something went wrong')}</div>}

      <RejectModal
        active={isModalActive}
        onClose={() => setModalActive(false)}
        comment={comment}
        setComment={setComment}
        error={commentError}
        onSubmit={rejectQuestionnaire}
        disabledButtons={isLoading}
      />
    </div>
  );
};
